import { CRAYO_ENTRY, money, crayoPlanTerms } from "@/lib/compare/crayo";
import type { CompareContent } from "@/lib/compare/types";

const hobby = CRAYO_ENTRY;
const AGREEMENT =
  "https://sf21-draftcdn-sg.ibytedtos.com/obj/ies-hotsoon-draft-sg/capcut/via_clause_user_agreement_en.html";
const HELP_TIERS = "https://www.capcut.com/help/difference-between-standard-and-pro-subscription";
const SOCIALRAILS = "https://socialrails.com/blog/capcut-pricing-guide";
const LICENSE_CLAUSE =
  "nonexclusive, perpetual, irrevocable, royalty-free, worldwide, transferable, sublicensable license";

export const CAPCUT_CONTENT: CompareContent = {
  slug: "crayo-vs-capcut",
  competitorSlug: "capcut",
  title: "Crayo vs CapCut",
  seoTitle: "Crayo vs CapCut: Pricing, Captions and Clipping Compared",
  seoDescription:
    "Crayo vs CapCut, with dated prices. CapCut has no pricing page; Pro is about $19.99/mo per third-party guides. Crayo publishes every plan and ranks clips.",
  cardVerdict: "Better than Crayo for free editing and mobile apps, behind on clipping and price transparency.",
  bestFor: "Clippers weighing a free editor against ranked clips",
  datePublished: "2026-10-09",
  dateModified: "2026-10-25",
  verdict:
    `As of October 25, 2026, Crayo is the better pick if you clip podcasts and streams into shorts: AutoClip ranks the moments, a face-aware camera reframes them, captions run in 56 languages, and the timeline editor and public API are on one ${money(hobby.monthly)} plan with published prices. CapCut is the better pick if you want a free editor, a native mobile or offline desktop app, or a much larger template and effects library. The evidence is below.`,
  pickCrayoIf: [
    "You clip podcasts and streams: AutoClip ranks the moments and cuts 1 to 20 clips per run with a face-aware camera, while CapCut's long-to-short tool documents no virality ranking.",
    "You want to know the price before you start: Crayo publishes every plan at crayo.ai/pricing and meters one credit per finished clip, while capcut.com/pricing returned a 404 on Oct 8, 2026.",
    "You caption in languages beyond CapCut's 20: Crayo transcribes 56 spoken languages on every plan, and CapCut lists auto captions as a Pro feature.",
  ],
  pickThemIf: [
    "You want a free editor. CapCut's free plan exports at 1080p with a watermark on templates and effects; Crayo has no free plan.",
    "You edit on a phone or offline. CapCut has native iOS, Android, Mac and Windows apps; Crayo is a browser app.",
    "You live in templates and effects, or you share straight to TikTok from the editor. CapCut's library is far larger and Crayo has no TikTok share.",
  ],
  wins: [
    {
      claim: "Crayo ranks moments before cutting; CapCut's long-to-short tool does not.",
      evidence:
        "CapCut Online's Long video to shorts tool cuts clips but documents no virality ranking as of Oct 8, 2026. Crayo AutoClip returns 1 to 20 ranked clips per run and Find Viral Moments lists up to 50 ranked moments to review before you spend a credit. Clip Cutter adds manual ranges, up to 50 per video, at no credit cost.",
      href: "https://www.capcut.com/resource/turn-long-video-to-short-video-with-ai",
    },
    {
      claim: "Crayo transcribes 56 spoken languages on every plan; CapCut lists up to 20 and puts auto captions on Pro.",
      evidence:
        "CapCut's own caption page says up to 20 languages, and its help page lists auto captions under Pro, checked Oct 8, 2026. Crayo's language picker has 56 entries and captions ship with 32 styles and keyword highlight on the entry plan.",
      href: HELP_TIERS,
    },
    {
      claim: "Crayo publishes every plan price; CapCut has no pricing page.",
      evidence:
        "capcut.com/pricing returned a 404 on Oct 8, 2026, and CapCut's help page defines Standard and Pro without a price. The figures on this page (Pro $19.99/mo or $179.99/yr) come from the socialrails pricing guide updated Jul 18, 2026 and the eesel article of May 20, 2026. Crayo's plan table at crayo.ai/pricing shows monthly and yearly prices for Hobby, Clipper and Pro with every tool on every plan.",
      href: HELP_TIERS,
    },
    {
      claim: "Crayo ships a 34-endpoint API with webhooks on every plan; CapCut has no public API.",
      evidence:
        "CapCut publishes no public API or developer documentation as of Oct 8, 2026; automation means hand-filled community templates. Crayo's API covers autoclip, exports, transcripts, voiceover and webhooks with one key per account, on every plan at crayo.ai/developers, and View Tracker is on the same plans.",
      href: "https://crayo.ai/developers",
    },
    {
      claim: "CapCut's served user agreement contains a broad content license clause, quoted here verbatim.",
      evidence:
        `The English user agreement CapCut serves, fetched Oct 8, 2026, states a "${LICENSE_CLAUSE}" over user content. The full clause is at the link; this page quotes it and does not interpret it.`,
      href: AGREEMENT,
    },
  ],
  concessions: [
    {
      claim: "CapCut is free to start and Crayo is not.",
      evidence:
        "Third-party guides (socialrails, Jul 18, 2026) describe a free plan with a 1080p cap and a watermark on templates, effects and the end card. Crayo has no free plan and no trial; the only free products are twelve browser tools.",
      href: SOCIALRAILS,
    },
    {
      claim: "CapCut runs natively on phones and offline on desktop.",
      evidence:
        "CapCut ships iOS, Android, Mac and Windows apps, and the desktop apps work offline. Crayo is a browser app at crayo.ai with a camera track, keyframes, zooms and speed ramps on every plan, but it needs a connection.",
      href: "https://www.capcut.com",
    },
    {
      claim: "CapCut's template and effects library is far larger, and it shares straight to TikTok.",
      evidence:
        "CapCut's homepage claims 100M downloads and the editor is built around templates, effects and a TikTok share step. Crayo offers five looks, four intensities and 32 caption styles, and you post exports yourself.",
      href: "https://www.capcut.com",
    },
    {
      claim: "CapCut has a Teams plan; Crayo has no team seats.",
      evidence:
        "Third-party guides put Teams at $24.99 per user per month or $214.99 per user per year, up to 4 members, checked Oct 8, 2026. Crayo accounts are single-seat on every plan.",
      href: SOCIALRAILS,
    },
  ],
  typicalMonth: {
    scenario: "One 60-minute source a week, eight clips each: 240 source minutes and 32 clips a month.",
    crayo: {
      plan: "Hobby",
      cost: `${money(hobby.monthly)}/mo or about ${money(hobby.yearlyPerMonth)}/mo yearly`,
      usage: `32 of ${hobby.credits} workflow credits and about 32 of ${hobby.exportMinutes} export minutes`,
      note: "Prices from the live plan table.",
    },
    them: {
      plan: "Pro",
      cost: "$19.99/mo or $179.99/yr (about $15/mo), third-party figure",
      usage:
        "Credits are spent on Motion and AI effects; CapCut does not publish what a long-to-short run or auto captions cost, so the count cannot be computed. Standard is mobile-only and lacks auto captions.",
      note: "Prices from socialrails (Jul 18, 2026) and eesel (May 20, 2026); CapCut publishes no pricing page.",
    },
  },
  test: {
    status: "scheduled",
    source: "A 45-minute two-person podcast recording owned by Crayo",
    plans: "CapCut Pro (Standard is mobile-only) and Crayo Clipper, same file, same day, defaults first then one tuned run each",
  },
  billingNote:
    "CapCut publishes no pricing page; the socialrails guide (Jul 18, 2026) and eesel article (May 20, 2026) agree Standard is monthly-only at about $9.99 and Pro is $19.99/mo or $179.99/yr, up from about $9.99/mo before the late-2025 restructure. CapCut's credits rule page says subscription credits are valid for one month, purchased credits for two years, and credits are non-refundable.",
  faqs: [
    {
      question: "Can CapCut turn a podcast or stream into ranked clips like Crayo?",
      answer:
        "Partly. CapCut Online has a Long video to shorts tool, but as of Oct 8, 2026 it documents no virality ranking, and Camera Tracking is a Pro feature with no auto reframe for clips documented. Crayo AutoClip takes a YouTube or TikTok link or a file up to 3 hours, returns 1 to 20 ranked clips of 15 to 120 seconds, and keeps the speaker framed in speaker, streamer, split or full layouts at 9:16. Find Viral Moments lists up to 50 ranked moments before you spend a credit, and Clip Cutter takes manual ranges at no credit cost. What Crayo lacks is a TikTok share step and a template feed; you post exports yourself.",
    },
    {
      question: "Is Crayo free like CapCut?",
      answer:
        `No. Crayo has no free plan and no trial. The entry plan is Hobby at ${money(hobby.monthly)} a month or ${money(hobby.yearlyTotal)} a year for ${hobby.credits} workflow credits, one per finished clip, and every tool is on every plan. The only free Crayo products are twelve browser tools at crayo.ai/features/free that need no account and never upload the file. CapCut's free plan is a full editor with a 1080p cap and a watermark on templates, effects and the end card, per third-party guides checked Oct 8, 2026. If a free editor is the requirement, CapCut wins that row.`,
    },
    {
      question: "What does CapCut Pro cost, and why did the price go up?",
      answer:
        `CapCut has no pricing page, so the figures are third-party. The socialrails guide (updated Jul 18, 2026) and eesel (May 20, 2026) put Pro at $19.99 a month or $179.99 a year, Standard at about $9.99 monthly-only, and Teams at $24.99 per user per month. Before the late-2025 restructure Pro was about $9.99 a month or $77.99 to $89.99 a year, and existing annual subscribers moved to the new rate around February 2026. Crayo Hobby is ${money(hobby.monthly)} a month or ${money(hobby.yearlyTotal)} a year, published at crayo.ai/pricing.`,
    },
    {
      question: "Which has better auto captions, Crayo or CapCut?",
      answer:
        "No side-by-side accuracy test has run yet, so this page states facts only. Crayo transcribes 56 spoken languages on every plan with 32 caption styles and keyword highlight. CapCut's caption page lists up to 20 languages, and its help page puts auto captions on the Pro tier. Both tools caption from an automatic transcript; in Crayo the caption layer is one of ten you can switch off per run. The scheduled test will count caption word errors on a fixed 60-second segment on both tools and publish the number whichever way it falls.",
    },
    {
      question: "Can I switch from CapCut to Crayo, or use both?",
      answer:
        "There is nothing to migrate: paste a YouTube or TikTok link or upload the file, pick a layout and intensity, and AutoClip returns ranked, captioned 9:16 clips you can open in the timeline editor. Using both also works: clip in Crayo, then open any clip in CapCut for template effects, stickers or a TikTok share, since Crayo exports a standard 1080p MP4 that CapCut imports on every platform. The reverse works too: cut in CapCut, then upload the result to Crayo for 56-language captions. CapCut's free plan adds a watermark to templates and effects, so a clip that only needs captions and a cut can be finished in Crayo at 1080p without the CapCut step.",
    },
    {
      question: "Does Crayo have a mobile app like CapCut?",
      answer:
        "Crayo is used in the browser at crayo.ai, and this page compares that browser app. CapCut ships native iOS and Android apps plus Mac and Windows desktop apps that work offline, and its help page describes Standard as a mobile-first tier. Standard is about $9.99 monthly-only per third-party guides, so it is the CapCut plan to compare for phone-only editing. If you edit mainly on a phone, or without a connection, CapCut wins that row. If you clip at a desk from a YouTube or TikTok link, Crayo's browser app needs no install.",
    },
    {
      question: "Does CapCut own my videos?",
      answer:
        `This page quotes CapCut's terms and does not interpret them. The English user agreement CapCut serves, fetched Oct 8, 2026, states a "${LICENSE_CLAUSE}" over user content. Read the full agreement at the link in the evidence section before uploading client work, and ask a lawyer if the wording matters to your business.`,
    },
    {
      question: "Is CapCut safe to use?",
      answer:
        "Stated factually: CapCut's user agreement names Bytedance Pte. Ltd. of Singapore as the contracting party under Singapore law. This page makes no claim about availability in any country. CapCut's credits rule page adds that subscription credits last one month, purchased credits two years, and none are refundable. Crayo publishes its plans at crayo.ai/pricing.",
    },
  ],
  changelog: [
    {
      date: "2026-10-09",
      text: "First published. CapCut prices checked Oct 8, 2026 via third-party guides because capcut.com/pricing returns a 404: Standard about $9.99/mo monthly-only, Pro $19.99/mo or $179.99/yr, Teams $24.99/user/mo.",
    },
    {
      date: "2026-10-25",
      text: `Crayo prices updated to the October 25, 2026 ladder: Hobby is ${crayoPlanTerms(hobby)}, with quarterly the default term at checkout. Verdicts and the corrections box re-checked at the new prices; competitor prices unchanged from the October 8 to 9 check.`,
    },
  ],
  relatedSpokes: ["crayo-vs-veed", "crayo-vs-descript", "crayo-vs-clipchamp"],
  featurePath: "/features/auto-clip",
  ctaHref: "https://crayo.ai/create/auto-clip",
  ctaLabel: "Clip a video now",
  targetQueries: [
    "capcut alternative",
    "capcut alternative for shorts",
    "capcut pro price increase",
    "capcut vs crayo",
    "crayo vs capcut",
    "capcut alternative reddit",
    "is capcut safe",
  ],
};
